import { JSD_FIELD_HELPTEXT } from '../../constants.tsx';
import {
	type AssociatedField,
	type ActiveLayoutItem,
	type Field,
	FIELD_LAYOUT_ITEM_TYPE,
	toLayoutItemKey,
	PANEL_LAYOUT_ITEM_TYPE,
} from '../../types/layout-items.tsx';
import type { FieldItemFragment as GraphqlFieldItem } from '../fragments/__generated_apollo__/FieldItemFragment';
import type { PanelItemFragment as GraphqlPanelItem } from '../fragments/__generated_apollo__/PanelItemFragment';
import { normalizeDefaultValue } from './default-value.tsx';
import { normalizeOptions } from './options.tsx';
import type { Transformers, ActiveLayoutItemTransformer } from './types.tsx';

// Empty string is not valid alias for name or description.
export const normalizeAlias = (alternateNameOrDescription?: string): string | undefined => {
	if (alternateNameOrDescription === '') {
		return undefined;
	}
	return alternateNameOrDescription;
};

const toField = (
	graphqlField: GraphqlFieldItem,
	containerType: string,
	transformers: Transformers,
): ActiveLayoutItem => {
	const field: Field = transformers.field(graphqlField, transformers);
	const { properties, options, required, defaultValue } = graphqlField;

	const normalizedOptions = normalizeOptions(field.type, options);

	const associatedField: AssociatedField = {
		...field,
		category: transformers.category(containerType),
		required,
		options: normalizedOptions,
		alternateDescription: properties ? normalizeAlias(properties[JSD_FIELD_HELPTEXT]) : undefined,
		defaultValue: normalizeDefaultValue(field.type, defaultValue, normalizedOptions),
		configuration: field.configuration
			? {
					...field.configuration,
					// needed as otherwise `inactiveFieldLozenge` gives a type error because it is `mixed`
					// we don't expect the server to return lozenge data as it's internationalized messages
					// https://atlassian.slack.com/archives/C0271N53BB4/p1633999472180600?thread_ts=1633919728.133600&cid=C0271N53BB4
					inactiveFieldLozenge: undefined,
				}
			: field.configuration,
	};

	return {
		key: toLayoutItemKey(FIELD_LAYOUT_ITEM_TYPE, associatedField.key),
		layoutItemType: FIELD_LAYOUT_ITEM_TYPE,
		data: associatedField,
		operations: field.operations,
		category: associatedField.category,
	};
};

const toPanel = (
	graphqlPanel: GraphqlPanelItem,
	containerType: string,
	transformers: Transformers,
): ActiveLayoutItem => {
	const panel = transformers.panel(graphqlPanel, transformers);
	const category = transformers.category(containerType);

	return {
		key: toLayoutItemKey(PANEL_LAYOUT_ITEM_TYPE, panel.key),
		layoutItemType: PANEL_LAYOUT_ITEM_TYPE,
		data: panel,
		operations: panel.operations,
		category,
	};
};

/**
 * Transform item data from GraphQL into a SWIFT compatible {@link ActiveLayoutItem} object.
 */
export const toActiveLayoutItem: ActiveLayoutItemTransformer = (
	graphqlItem: GraphqlFieldItem | GraphqlPanelItem,
	containerType: string,
	transformers: Transformers,
) => {
	switch (graphqlItem.__typename) {
		case 'JiraIssueLayoutFieldItemConfiguration':
			return toField(graphqlItem, containerType, transformers);
		case 'JiraIssueLayoutPanelItemConfiguration':
			return toPanel(graphqlItem, containerType, transformers);
		default:
			return null;
	}
};
